// See messages/README.md for how to add strings.
// The /train page (pages/train.js) and its flow in components/train/: the
// upload step (TrainUploadStage, Sample, ClipEditor), the name step
// (TrainInfoStage), the demo under it (components/CustomModelDemo) and the
// failed voice's "Try again" (RetryTraining, shown in the library).
// Lengths arrive already formatted as clocks ("6:00") in {time}, {have} etc.
export const train = {
  en: {
    "train.seo.title": "Train Custom AI Voices | Jammable",
    "train.seo.description":
      "Train your own custom AI voices on Jammable. Upload audio files, train a voice model, and share it with the community or keep it private.",
    "train.h1": "Train a voice",
    "train.sub":
      "A few minutes of clean singing or speaking in, a voice you can use in your covers out.",
    "train.voicesTrained": "{count} voices trained",

    "train.faq.title": "Common questions",
    "train.faq.dataset.q": "What is a dataset?",
    "train.faq.dataset.a1": "A set of audio files containing vocals.",
    "train.faq.dataset.a2":
      "At least {min} minutes of singing or speaking, preferably 10+.",
    "train.faq.dataset.a3": "MP3, WAV or M4A format.",
    "train.faq.private.q": "Will my custom voice be private?",
    "train.faq.private.a":
      "Yes! Only you will be able to access, see and use your custom voice model if you choose to keep it private!",
    "train.faq.time.q": "How long will my voice take to train?",
    "train.faq.time.a":
      "It depends on how much audio you add: usually {short} for 10 minutes of audio, {long} for an hour of it. You can close the page; we'll email you when it's ready.",
    "train.facts.title": "Training at a glance",
    "train.facts.min.value": "{min} min",
    "train.facts.min.label":
      "of audio at the least. {better} minutes or more sounds better, and up to {max} are used.",
    "train.facts.time.value": "~{m} min",
    "train.facts.time.label":
      "to train on 10 minutes of audio. An hour of audio takes {long}.",
    "train.facts.formats.label":
      "Singing or speech in one voice. Silence is cut for you.",
    "train.facts.plan.value": "Creator",
    "train.facts.plan.label":
      "and Power include training. On other plans, each voice uses one training pass.",
    "train.facts.private": "Keep it private or share it with the community",
    "train.facts.rvc":
      "Already have an RVC model? Upload its .pth and .index files from your Library",
    "train.facts.noInstall": "Nothing to install, no graphics card needed",
    "train.facts.email": "We email you when it's ready",
    "train.faq.plan.q": "Which plan includes voice training?",
    "train.faq.plan.a":
      "Creator and Power train voices without passes. On other plans, each voice uses one training pass.",
    "train.faq.rvc.q": "Can I upload an RVC model I already have?",
    "train.faq.rvc.a":
      "Yes. Upload its .pth and .index files from your Library instead of training a new one.",
    "train.faq.audio.q": "What audio works best?",
    "train.faq.audio.a":
      "One voice, dry and clean: no music, no effects, no long silences. Vary what you sing: low and high, soft and loud.",

    // How long training takes (lib/train/eta minutesLabel, same rounding).
    "train.eta.minutes": "about {m} minutes",
    "train.eta.hour": "about {h} hour",
    "train.eta.hours": "about {h} hours",
    "train.eta.hourMinutes": "about {h} hour {m} minutes",
    "train.eta.hoursMinutes": "about {h} hours {m} minutes",
    "train.minutes": "{n} minutes",

    // Shared by both steps' summary cards
    "train.samples": "Samples",
    "train.filesOne": "1 file · {time}",
    "train.filesMany": "{n} files · {time}",
    "train.passesLeft": "Training passes left",
    "train.unlimited": "Unlimited",
    "train.nameIt": "Name it",
    "train.creatorShort": "Training comes with Creator.",

    // Step 1: upload or record
    "train.upload.invalidType": "Invalid file type.",
    "train.upload.skippedOne":
      "Skipped {names}: already added. The same audio twice doesn't help your voice, so it only counts once.",
    "train.upload.skippedMany":
      "Skipped {names}: already added. The same audio twice doesn't help your voice, so it only counts once.",
    "train.upload.nFiles": "{n} files",
    "train.upload.someInvalid": "One or more files are invalid.",
    "train.upload.failed": "Upload failed",
    "train.upload.needsPlan": "Needs a plan to upload",
    "train.upload.tooManyLabel": "Too many uploads right now, try again soon",
    "train.upload.tooManyToast":
      "Too many uploads right now. Try again in a few minutes.",
    "train.upload.analysing":
      "Still finding the silence in your files. One moment.",
    "train.upload.blocked":
      "Some files are blocked. Please remove them to continue.",
    "train.upload.errored":
      "Some files have errors. Please remove them to continue.",
    "train.upload.processing":
      "Some files are still processing. Please wait for them to finish.",
    "train.upload.tooShort":
      "You need {min} of singing or speaking, without the silence. You have {have}.",
    "train.upload.tooLong":
      "Up to {max} of audio. You have {have}: remove a file or cut some.",
    "train.upload.title": "Add your voice",
    "train.upload.ofMin": "of {min}",
    "train.upload.overLimit": "{over} over the {n} minute limit",
    "train.upload.ready": "Ready ✓",
    "train.upload.addMore": "Add {left} more",
    "train.upload.short":
      "Voices usually sound better with {n}+ minutes. You can train now, or add {left} more if you have it.",
    "train.upload.uploadFiles": "Upload files",
    "train.upload.record": "Record",
    "train.upload.addIt": "Add it",
    "train.upload.recordHint": "Sing or talk. Record as many as you like.",
    "train.upload.blockedFrom": "Blocked - content from {label}",
    "train.upload.gettingReady": "Getting it ready...",
    "train.upload.ofVoice": "{time} of voice",
    "train.upload.removed": "{time} removed",
    "train.upload.uploading": "uploading...",
    "train.upload.done": "Done",
    "train.upload.trim": "Trim",
    "train.upload.retry": "Retry",
    "train.upload.remove": "Remove {name}",
    "train.upload.silenceNote":
      "Silence is removed for you. Purple is what your voice learns from.",
    "train.upload.tip1.title": "Just one voice",
    "train.upload.tip1.sub": "No backing singers or crowd",
    "train.upload.tip2.title": "Quiet room",
    "train.upload.tip2.sub": "No echo, no background noise",
    "train.upload.tip3.title": "More is better",
    "train.upload.tip3.sub": "Different notes and words",
    "train.upload.nextHint": "Next: a name, a picture, and who can use it.",
    "train.upload.cardTitle": "Your samples",
    "train.upload.footnote":
      "At least {min} of singing or speaking ({rec}+ sounds better), up to {max}. Silence and repeated files don't count; silence is cut for you.",
    "train.upload.footnoteNoRec":
      "At least {min} of singing or speaking, up to {max}. Silence and repeated files don't count; silence is cut for you.",
    "train.upload.creatorNote":
      "Training comes with Creator. Upload your vocals first and decide at the end; you'll see the price before you pay.",
    "train.upload.continue": "Continue",

    // A sample's play button and the trim editor
    "train.sample.play": "Play {label}",
    "train.sample.pause": "Pause {label}",
    "train.clip.keptOf": "kept of {total}",
    "train.clip.silenceCut": "{time} silence cut",
    "train.clip.cutByYou": "{time} cut by you",
    "train.clip.cut": "cut",
    "train.clip.play": "Play",
    "train.clip.stop": "Stop",
    "train.clip.keepIt": "Keep it",
    "train.clip.cutIt": "Cut it",
    "train.clip.cancel": "Cancel",
    "train.clip.hint":
      "Drag across the part you don't want, then cut it. Tap to listen.",
    "train.clip.undo": "Undo my cuts",

    // Step 2: name, picture, settings, start
    "train.info.samplesAdded": "Samples added",
    "train.info.edit": "Edit",
    "train.info.namePlaceholder": "Voice name, e.g. My voice",
    "train.info.descPlaceholder":
      "Description - let the community know more about how the voice was trained...",
    "train.info.whoCanUse": "Who can use it",
    "train.info.private": "Private, only me",
    "train.info.public": "Public in the library",
    "train.info.cleanUp": "Clean up the samples",
    "train.info.removeInstrumentals": "Remove instrumentals",
    "train.info.removeInstrumentalsSub":
      "Cleans the input data for any non-vocals in the dataset.",
    "train.info.removeReverb": "Remove reverb",
    "train.info.removeReverbSub":
      "Cleans reverb from any audio in the dataset.",
    "train.info.cardTitle": "Ready to train",
    "train.info.usuallyReadyIn": "Usually ready in",
    "train.info.upTo2h": "Up to 2 hours",
    "train.info.closePage":
      "Training runs on our side, so you can close the page.",
    "train.info.starting": "Starting...",
    "train.info.start": "Start training",
    "train.info.orGetPass": "Or get a training pass",
    "train.info.getMorePasses": "Get more training passes",
    "train.info.usesOnePass": "Uses 1 training pass",
    "train.info.started": "Training Started",
    "train.info.onCreator": "You're on Creator. Starting your training...",
    "train.info.legal":
      "By proceeding, you are giving Jammable the right to use the content you upload and CONFIRM THAT THIS CONTENT IS YOURS AND DOES NOT CONTAIN THIRD PARTY COPYRIGHT MATERIAL.",
    "train.info.agreeTerms": "Please agree to our Terms of Use!",
    "train.info.chooseName": "Please choose a voice name!",
    "train.info.uploadImage": "Please upload an image!",
    "train.info.invalidName": "Invalid model name. No special characters",
    "train.info.signIn": "User must be signed in",
    "train.info.errorStarting": "Error starting training",
    "train.info.errorSignedUrl": "Error getting signed url",

    // Demo under step 1 (components/CustomModelDemo)
    "train.demo.title": "Hear a trained voice",
    "train.demo.by": "By Jammable Power User",
    "train.demo.data.label": "Training data",
    "train.demo.data.note": "A few minutes of a voice",
    "train.demo.input.label": "Input song",
    "train.demo.input.note": "The song it will sing",
    "train.demo.output.label": "Final output",
    "train.demo.output.note": "Sung in the trained voice",
    "train.demo.play": "Play {label}",
    "train.demo.pause": "Pause {label}",

    // Failed voice card (RetryTraining). The refuse.* lines mirror the
    // messages in lib/train/retry.ts, which the API sends in English.
    "train.retry.trainAgain": "Train again",
    "train.retry.tryAgain": "Try again",
    "train.retry.orNewSamples": "Or train it with new samples",
    "train.retry.retrying": "Training again. We'll email you when it's ready.",
    "train.retry.couldntStart": "Couldn't start training. Try again.",
    "train.retry.confirmUnlimited":
      "We'll train it again on the same samples. Included in your plan.",
    "train.retry.confirmOutCreator":
      "Trying again uses a training pass, and you're out. Training comes with Creator.",
    "train.retry.confirmOut":
      "Trying again uses a training pass, and you're out.",
    "train.retry.confirmPass":
      "We'll train it again on the same samples. Uses 1 training pass (you have {passes}); if it fails again, you get it back.",
    "train.retry.cancel": "Cancel",
    "train.retry.seeCreator": "See Creator",
    "train.retry.getPass": "Get a training pass",
    "train.retry.orGetPass": "Or get a training pass",
    "train.retry.starting": "Starting...",
    "train.retry.usePass": "Use a pass and try again",
    "train.retry.refuse.cleanVocals":
      "Trying again would fail the same way. Train it again with clean vocals: no music, no long silences.",
    "train.retry.refuse.formats":
      "Trying again would fail the same way. Train it again with MP3, WAV or M4A files of clear singing or speech.",
    "train.retry.refuse.newSamples":
      "Trying again would fail the same way. Train it again with new samples.",
    "train.retry.refuse.saveAs":
      "Trying again would fail the same way. Save it as MP3 or WAV and train it again.",
    "train.retry.refuse.filesMissing": "Train it again with new samples.",
    "train.retry.refuse.retryCap":
      "This voice has been tried {n} times. Train it again, ideally with different samples.",

    // TrainFinalStage (not currently rendered)
    "train.final.confirm": "Confirm",
    "train.final.back": "Back",
    "train.final.next": "Next",
  },
};
